import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'

import { useApiClient } from 'clients/yogi-api'
import type { FilterControlProps } from 'features/charts-v2'
import { buildFiltersPanel } from 'features/charts-v2/utils'
import { fetchFilterOptionsV2 } from 'features/filters/service'
import type { FilterService } from 'features/filters/service/service'
import type { FilterOption } from 'features/filters/types'
import type {
  BenchmarkingAgentDetail,
  FilterCriterion,
} from 'features/trend-detection/types'
import {
  buildAgentFiltersV2,
  buildBenchmarkingFiltersV2,
} from 'features/trend-detection/utils'
import { useAgentFilterPanel } from 'src/hooks/use-agent-filter-panel'

export interface BenchmarkingResult {
  title: string
  summary: string
  report: string
}

/**
 * Infers the entity-type radio from a hydrated agent's focus-group filter
 * field names. A fallback, not a real read: `entity_dimension` isn't
 * persisted by either the customer create or update endpoint today (see
 * `hydrateFromAgent`'s doc comment), so an agent with an empty focus group
 * resolves to `null` — no dimension selected — same as the deleted
 * `useEditBenchmarkingAgent.initFromAgent` this mirrors.
 */
export function inferEntityTypeFromFocusFilters(
  focusGroupFilters: readonly FilterCriterion[],
): 'brand' | 'product_hierarchy' | null {
  if (focusGroupFilters.some((f) => f.field === 'brand')) return 'brand'
  if (focusGroupFilters.some((f) => f.field === 'product_hierarchy')) {
    return 'product_hierarchy'
  }
  return null
}

export interface UseBenchmarkingSetupParams {
  filterService: FilterService
  projectId: string | null
  /** The edited agent's `filter_value_labels` — see `useAgentFilterPanel`'s
   *  `valueLabels`. Absent on create. */
  valueLabels?: Readonly<Record<string, string>> | null
}

export interface ValidateFiltersResult {
  valid: boolean
  errors: string[]
}

export interface UseBenchmarkingSetupResult {
  agentName: string
  setAgentName: (name: string) => void
  entityType: 'brand' | 'product_hierarchy' | null
  setEntityType: (type: 'brand' | 'product_hierarchy') => void
  cohortFilters: FilterCriterion[]
  setCohortFilters: (filters: FilterCriterion[]) => void
  focusGroupFilters: FilterCriterion[]
  setFocusGroupFilters: (filters: FilterCriterion[]) => void
  emailNotifications: boolean
  setEmailNotifications: (value: boolean) => void
  focusGroupFilterOptions: FilterOption[] | undefined
  focusGroupFilterOptionsLoading: boolean
  /** Prop bag for the shared Issue-Agent-style Filters section — see
   * `useAgentFilterPanel`. Replaces the old "Universe" `cohortSetupFilters`. */
  filterControlProps: FilterControlProps
  isValidatingFilters: boolean
  validateFilters: () => Promise<ValidateFiltersResult>
  isSaving: boolean
  saveAgent: () => Promise<string>
  reset: () => void
  /** Populates all fields from an existing agent, for the edit-mode create
   * form. Mirrors the deleted `useEditBenchmarkingAgent.initFromAgent`, plus
   * hydrating `emailNotifications` (which that hook never restored). */
  hydrateFromAgent: (agent: BenchmarkingAgentDetail) => void
}

const _TIMEOUT_MS = 5 * 60 * 1000

export function useBenchmarkingSetup({
  filterService,
  projectId,
  valueLabels,
}: UseBenchmarkingSetupParams): UseBenchmarkingSetupResult {
  const apiClient = useApiClient()
  // Starts empty, not pre-filled — the placeholder carries the default
  // string. See use-agent-create-form.ts's identical treatment.
  const [agentName, setAgentName] = useState('')
  // Defaults to Brands rather than unselected — the wizard should be usable
  // without an initial click. See setEntityType/reset for the same default.
  const [entityType, setEntityTypeState] = useState<
    'brand' | 'product_hierarchy' | null
  >('brand')
  const [cohortFilters, setCohortFilters] = useState<FilterCriterion[]>([])
  const [focusGroupFilters, setFocusGroupFilters] = useState<FilterCriterion[]>(
    [],
  )
  const [debouncedCohortFilters, setDebouncedCohortFilters] = useState<
    FilterCriterion[]
  >([])
  useEffect(() => {
    const id = setTimeout(() => setDebouncedCohortFilters(cohortFilters), 400)
    return () => clearTimeout(id)
  }, [cohortFilters])
  const [isValidatingFilters, setIsValidatingFilters] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // Default emails OFF in the creation wizard — mirrors the issue-agent
  // setup default. The schema default for UserBenchmarkingAgent is still
  // True (so default agents stay default-on for project members), so
  // saveAgent must always PUT the user's choice to align the DB row with
  // what the user actually picked here.
  const [emailNotifications, setEmailNotifications] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const {
    data: focusGroupFilterOptions,
    isLoading: focusGroupFilterOptionsLoading,
  } = useQuery({
    queryKey: [
      'filter-options-benchmarking-focus',
      projectId,
      entityType,
      JSON.stringify(debouncedCohortFilters),
    ],
    queryFn: async () => {
      if (!projectId || !entityType) return undefined
      // Not faceted: the cohort may already filter on the entity field itself,
      // and the focus picker should count within the cohort as filtered.
      return fetchFilterOptionsV2(
        projectId,
        buildFiltersPanel(buildAgentFiltersV2(debouncedCohortFilters).filters),
        filterService,
        { fields: [entityType], faceted: false },
      )
    },
    enabled: !!projectId && !!entityType,
    refetchOnWindowFocus: false,
    staleTime: 300000,
  })

  const { filterControlProps } = useAgentFilterPanel({
    projectId,
    criteria: cohortFilters,
    onCriteriaChange: setCohortFilters,
    valueLabels,
  })

  const setEntityType = useCallback(
    (type: 'brand' | 'product_hierarchy') => {
      if (type !== entityType && focusGroupFilters.length > 0) {
        setFocusGroupFilters([])
      }
      setEntityTypeState(type)
    },
    [entityType, focusGroupFilters.length],
  )

  const validateFilters =
    useCallback(async (): Promise<ValidateFiltersResult> => {
      if (!projectId) return { valid: true, errors: [] }
      setIsValidatingFilters(true)
      try {
        const { data } = await apiClient.post(
          `/benchmarking/agents/validate-filters?proj_uuid=${projectId}`,
          buildBenchmarkingFiltersV2(cohortFilters, focusGroupFilters),
        )
        return data as ValidateFiltersResult
      } catch {
        toast.warn(
          'Filter validation unavailable — please verify your filters manually.',
        )
        return { valid: true, errors: [] }
      } finally {
        setIsValidatingFilters(false)
      }
    }, [apiClient, projectId, cohortFilters, focusGroupFilters])

  const saveAgent = useCallback(async (): Promise<string> => {
    if (!projectId) throw new Error('No project selected')
    const controller = new AbortController()
    abortControllerRef.current = controller
    const timeoutId = setTimeout(() => controller.abort(), _TIMEOUT_MS)
    setIsSaving(true)
    try {
      const { data: agentData } = await apiClient.post(
        `/benchmarking/agents?proj_uuid=${projectId}`,
        {
          name: agentName,
          ...buildBenchmarkingFiltersV2(cohortFilters, focusGroupFilters),
        },
        { signal: controller.signal },
      )
      const agentUuid = (agentData as { uuid: string }).uuid
      // Always persist the email-preference choice. The schema default for
      // UserBenchmarkingAgent is True but the wizard defaults the toggle
      // to False (matching issue agents), so we can't rely on the schema
      // default — we have to explicitly send whichever value the user
      // picked. The PUT is best-effort: a failure surfaces a warning toast
      // and the user can toggle from the detail page.
      try {
        await apiClient.put(
          `/benchmarking/agents/${agentUuid}/email-preference?proj_uuid=${projectId}`,
          { email_notifications: emailNotifications },
          { signal: controller.signal },
        )
      } catch {
        toast.warn(
          "Agent created, but we couldn't save your email preference. Toggle it on the agent page.",
        )
      }
      return agentUuid
    } finally {
      clearTimeout(timeoutId)
      setIsSaving(false)
    }
  }, [
    apiClient,
    projectId,
    agentName,
    cohortFilters,
    focusGroupFilters,
    emailNotifications,
  ])

  const reset = useCallback(() => {
    setAgentName('')
    setEntityTypeState('brand')
    setCohortFilters([])
    setFocusGroupFilters([])
    setEmailNotifications(false)
  }, [])

  const hydrateFromAgent = useCallback((agent: BenchmarkingAgentDetail) => {
    setAgentName(agent.name)
    setCohortFilters(agent.config.cohort_filters)
    setFocusGroupFilters(agent.config.focus_group_filters)
    setEntityTypeState(
      inferEntityTypeFromFocusFilters(agent.config.focus_group_filters),
    )
    setEmailNotifications(agent.email_notifications)
  }, [])

  return {
    agentName,
    setAgentName,
    entityType,
    setEntityType,
    cohortFilters,
    setCohortFilters,
    focusGroupFilters,
    setFocusGroupFilters,
    emailNotifications,
    setEmailNotifications,
    focusGroupFilterOptions,
    focusGroupFilterOptionsLoading: focusGroupFilterOptionsLoading ?? false,
    filterControlProps,
    isValidatingFilters,
    validateFilters,
    isSaving,
    saveAgent,
    reset,
    hydrateFromAgent,
  }
}
